import { FC, useMemo } from "react";
import { Button } from "@radix-ui/themes";
import { ColumnDef } from "@tanstack/react-table";
import { Table } from "@/components/table/table";
import { DataImportItemSummarySchema } from "@/domains/openapi/types";
import { orderBy } from "lodash-es";
import { DateTime } from "luxon";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { TextFieldRow } from "@/components/table/text-field-row";

interface DataImportItemsTableProps {
  data: DataImportItemSummarySchema[];
  onViewDataImportItem: (id: string) => void;
  pageSize: number;
}

export const DataImportItemsTable: FC<DataImportItemsTableProps> = ({
  data,
  onViewDataImportItem,
  pageSize,
}) => {
  const columns = useMemo<ColumnDef<DataImportItemSummarySchema>[]>(
    () => [
      {
        header: "ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button variant="outline" size="1" onClick={() => onViewDataImportItem(row.original.id)}>
            View
          </Button>
        ),
      },
      {
        header: "Internal Created At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.internal_created_at} />,
      },
      {
        header: "Imported At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.imported_at} />,
      },
      {
        header: "Item Kind",
        cell: ({ row }) => <TextFieldRow value={row.original.item_kind} />,
      },
      {
        header: "Item ID",
        cell: ({ row }) => <TextFieldRow value={row.original.item_id} />,
      },
    ],
    [onViewDataImportItem]
  );

  const orderedData = useMemo(
    () =>
      orderBy(
        data,
        x =>
          x.internal_created_at ? DateTime.fromISO(x.internal_created_at).toMillis() : -Infinity,
        "desc"
      ),
    [data]
  );
  return <Table data={orderedData} columns={columns} pageSize={pageSize} />;
};
